import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { FontIcon } from '@fluentui/react';
import SConsciousApiService from '../services/SConsciousApiService';
import Camera from './Camera';

interface BrandSuggestion {
    brand: string;
    corporation: string;
}

interface BrandPickerProps {
    itemId: string;
    receiptId: string;
    description: string;
    merchantName: string;
    merchantCity?: string;
    userId: string;
    initialBrand?: string;
    productIdentifier?: string;
    productType?: string;
    onSaved: (brand: string, corporation: string) => void;
    onCancel: () => void;
}

const _api = new SConsciousApiService();

const BrandPicker: React.FC<BrandPickerProps> = ({ itemId, receiptId, description, merchantName, merchantCity, userId, initialBrand, productIdentifier, productType, onSaved, onCancel }) => {
    const [suggestions, setSuggestions] = useState<BrandSuggestion[]>([]);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState<BrandSuggestion | null>(null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [showCamera, setShowCamera] = useState(false);
    const [scanning, setScanning] = useState(false);
    const [failedScanAttempts, setFailedScanAttempts] = useState(0);
    const [scanRewarded, setScanRewarded] = useState(false);
    const [scanRewardMessage, setScanRewardMessage] = useState<string | null>(null);

    const MAX_SCAN_ATTEMPTS = 3;

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        _api.suggestItemBrands(description, merchantName, merchantCity, userId, productIdentifier, productType).then(result => {
            if (!cancelled) {
                setSuggestions(result);
                setLoading(false);
            }
        });
        return () => { cancelled = true; };
    }, [description, merchantName, merchantCity, userId]);

    const handleSave = async () => {
        const brand = selected?.brand ?? '';
        const corporation = selected?.corporation ?? '';
        if (!brand) return;
        setSaving(true);
        setError(null);
        const ok = await _api.updateItemBrand(itemId, receiptId, brand, corporation, userId);
        setSaving(false);
        if (ok) {
            onSaved(brand, corporation);
        } else {
            setError('Could not save. Please try again.');
        }
    };

    const handlePhotoCapture = async (imgSrc: string) => {
        setShowCamera(false);
        setScanning(true);
        setError(null);
        setScanRewardMessage(null);
        const result = await _api.scanProductPhoto(userId, imgSrc, itemId, description);
        setScanning(false);
        if (result === 'rate_limited') {
            setError('You have reached the daily scan limit. Try again tomorrow.');
            return;
        }
        if (result && result.suggestions.length > 0) {
            if (!result.relevant) {
                setError(`That doesn't look like the right product. Please scan the actual "${description}" packaging.`);
                return;
            }
            // Merge photo suggestions at the front, dedup by brand name
            setSuggestions(prev => {
                return [...result.suggestions, ...prev.filter(s => !result.suggestions.find(r => r.brand.toLowerCase() === s.brand.toLowerCase()))];
            });
            setSelected(result.suggestions[0]);
            if (result.rewarded && !scanRewarded) {
                setScanRewarded(true);
                setScanRewardMessage('+2 sCoN\'s earned for scanning this product!');
            }
        } else {
            // suggestions is empty — only count as a real attempt if the scan was of a relevant product
            if (!result || !result.relevant) {
                setError(`That doesn't look like the right product. Please scan the actual "${description}" packaging.`);
                return;
            }
            const newFailedCount = failedScanAttempts + 1;
            setFailedScanAttempts(newFailedCount);
            if (newFailedCount >= MAX_SCAN_ATTEMPTS) {
                const consoled = await _api.claimBrandScanConsolation(userId, itemId);
                if (consoled && !scanRewarded) {
                    setScanRewarded(true);
                    setScanRewardMessage('+2 sCoN\'s earned — thanks for trying!');
                }
                setError('Could not identify a brand after 3 attempts. Keep selecting manually!');
            } else {
                const remaining = MAX_SCAN_ATTEMPTS - newFailedCount;
                setError(`Could not identify a brand from this photo. Try a different angle. (${remaining} attempt${remaining === 1 ? '' : 's'} left)`);
            }
        }
    };

    const activeBrand = selected?.brand ?? null;

    return (
        <>
            {/* Camera overlay — rendered via portal to escape overflow/stacking context of parent containers */}
            {showCamera && ReactDOM.createPortal(
                <Camera onCapture={handlePhotoCapture} onClose={() => setShowCamera(false)} />,
                document.body
            )}

        <div style={{
            background: 'rgba(0,0,0,0.06)',
            borderRadius: 12,
            padding: '14px 16px',
            marginTop: 4,
        }}>
            <div style={{ fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10, opacity: 0.6 }}>
                Select brand for &ldquo;{description}&rdquo;
            </div>

            {loading ? (
                <div style={{ fontSize: '0.82rem', opacity: 0.55, marginBottom: 10 }}>Getting suggestions…</div>
            ) : (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
                    {suggestions.map(s => (
                        <button
                            key={s.brand}
                            onClick={() => setSelected(s)}
                            style={{
                                fontFamily: 'inherit',
                                background: selected?.brand === s.brand ? '#000' : 'rgba(0,0,0,0.10)',
                                color: selected?.brand === s.brand ? '#ffcc00' : '#000',
                                border: 'none',
                                borderRadius: 20,
                                padding: '6px 14px',
                                fontSize: '0.82rem',
                                fontWeight: 600,
                                cursor: 'pointer',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'flex-start',
                                lineHeight: 1.3,
                            }}
                        >
                            <span>{s.brand}</span>
                            {s.corporation && (
                                <span style={{ fontSize: '0.7rem', opacity: 0.55, fontWeight: 400 }}>{s.corporation}</span>
                            )}
                        </button>
                    ))}
                </div>
            )}

            {scanning && (
                <div style={{ fontSize: '0.82rem', opacity: 0.55, marginBottom: 10 }}>Scanning photo…</div>
            )}

            {scanRewardMessage && (
                <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#000', background: '#ffcc00', borderRadius: 8, padding: '6px 10px', marginBottom: 8 }}>
                    {scanRewardMessage}
                </div>
            )}

            {error && (
                <div style={{ fontSize: '0.78rem', color: '#e53935', marginBottom: 8 }}>{error}</div>
            )}

            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <button
                    onClick={handleSave}
                    disabled={!activeBrand || saving}
                    style={{
                        fontFamily: 'inherit',
                        flex: 1,
                        background: '#000',
                        color: '#ffcc00',
                        border: 'none',
                        borderRadius: 20,
                        padding: '8px 0',
                        opacity: saving ? 0.35 : 1,
                        cursor: activeBrand && !saving ? 'pointer' : 'not-allowed',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <FontIcon iconName="Save" style={{ fontSize: '1rem' }} />
                </button>
                <button
                    onClick={() => setShowCamera(true)}
                    disabled={saving || scanning || failedScanAttempts >= MAX_SCAN_ATTEMPTS}
                    title={failedScanAttempts >= MAX_SCAN_ATTEMPTS ? 'No more scan attempts remaining' : 'Take a photo of the product to identify the brand'}
                    style={{
                        fontFamily: 'inherit',
                        flex: 1,
                        background: saving || scanning || failedScanAttempts >= MAX_SCAN_ATTEMPTS ? 'rgba(0,0,0,0.12)' : '#000',
                        color: saving || scanning || failedScanAttempts >= MAX_SCAN_ATTEMPTS ? 'rgba(0,0,0,0.3)' : '#ffcc00',
                        border: 'none',
                        borderRadius: 20,
                        padding: '8px 0',
                        cursor: saving || scanning || failedScanAttempts >= MAX_SCAN_ATTEMPTS ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <FontIcon iconName="Camera" style={{ fontSize: '1rem' }} />
                </button>
                <button
                    onClick={onCancel}
                    disabled={saving}
                    style={{
                        fontFamily: 'inherit',
                        flex: '0 0 calc(25% - 30px)',
                        marginLeft: 16,
                        background: 'rgba(0,0,0,0.06)',
                        color: 'rgba(0,0,0)',
                        border: 'none',
                        borderRadius: 20,
                        padding: '8px 0',
                        cursor: saving ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <FontIcon iconName="Cancel" style={{ fontSize: '1rem' }} />
                </button>
            </div>
        </div>
        </>
    );
};

export default BrandPicker;
